import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Link } from "react-router-dom";
import { API } from "../../Common/lib/api";
import { Storage } from "../../Common/lib/storage";
import { Disclosure } from "@headlessui/react";
import { getUserSession } from "../../Common/lib/Session";
import { isProjectOwnedByUser } from "../../Common/lib/projectOwnership";
import ProjectArea from "../DataVisualization/MetaVisualization/MetaProjectAreaMap";
import "leaflet/dist/leaflet.css";
import useDocumentTitle from "../../Common/useDocumentTitle";
import { MapContainer, TileLayer } from "react-leaflet";
import "leaflet-area-select";
import DoiLogo from "../../images/DOI_logo.png";

import { FiPaperclip } from "react-icons/fi";
import {
  HiOutlineCalendar,
  HiOutlineDownload,
  HiLockClosed,
  HiOutlinePlusCircle,
  HiOutlineMinusCircle,
  HiLockOpen,
  HiExternalLink,
} from "react-icons/hi";

import LockProjectModal from "./Modals/lockProjectModal";
import UnlockProjectModal from "./Modals/unlockProjectModal";
import RestoreProjectModal from "./Modals/restoreProjectModal";

export default function MetadataDisplay({ printRef, project, isModerator, user }) {
  const [meta, setMeta] = useState({});
  const [files, setFiles] = useState([]);
  const [openLockModal, setOpenLockModal] = useState(false);
  const [openUnlockModal, setOpenUnlockModal] = useState(false);
  const [openRestoreModal, setOpenRestoreModal] = useState(false);

  useDocumentTitle(meta.title);

  let history = useHistory();

  // Used to display license links to avoid using repeat <a> tags.
  const licenses = {
    '"CC BY-NC-SA 4.0"': {
      url: "https://creativecommons.org/licenses/by-nc-sa/4.0/deed.en",
    },
    '"CC BY-SA 4.0"': {
      url: "https://creativecommons.org/licenses/by-sa/4.0/",
    },
    '"CC BY 4.0"': {
      url: "https://creativecommons.org/licenses/by/4.0/",
    },
    '"CC BY-ND 4.0"': {
      url: "https://creativecommons.org/licenses/by-nd/4.0/",
    },
    '"CC BY-NC-ND 4.0"': {
      url: "https://creativecommons.org/licenses/by-nc-nd/4.0/",
    },
    '"OGL-Canada-2.0"': {
      url: "https://open.canada.ca/en/open-government-licence-canada",
    },
    '"OGL-UK-2.0"': {
      url: "https://www.nationalarchives.gov.uk/doc/open-government-licence/version/2/",
    },
    '"BaySys"': {
      url: "https://umanitoba.ca/earth-observation-science/research/hudson-bay-system-study-baysys",
    },
  };

  useEffect(() => {
    try {
      if (project) {
        setMeta(project);
        getFiles();
      }
    } catch (e) { }
  }, [project]);


  /**
   * Sets the files state with the keys and sizes for all files associated with the project.
   */
  const getFiles = async () => {
    //Gets Links For File Downloads
    let files = [];
    let keys = JSON.parse(project.attachmentKeys);
    let sizes = JSON.parse(project?.fileSizes);

    if (keys) {
      // Go through all keys and create an object for each containing file name, key description, link, and size and then add to 'files' array. 'files' array then used in getDownloadableFiles() to show all files.
      for (let i = 0; i < keys.length; i++) {
        let file = {
          name: typeof keys[i] === "object" ? keys[i].key.split("/")[1] : keys[i].split("/")[1],
          description: typeof keys[i] === "object" ? keys[i].description : "",
          downloadLink: typeof keys[i] === "object" ? await getDownloadLink(keys[i].key) : await getDownloadLink(keys[i]),
          size: sizes[i],
        };

        files.push(file);
      }
    }
    setFiles(files);
  };

  /**
   * Retrieves download link for a file using key.
   * @param {String} key File S3 key
   * @returns File download link
   */
  async function getDownloadLink(key) {
    try {
      const result = await Storage.get(key);
      return result;
    } catch (err) {
      // Error handled silently
    }
  }

  /**
   * Handles locking / unlocking the project to prevent the publisher from editing the project metadata.
   */
const toggleLock = async () => {
  if (!meta?.id) return;

  const newLocked = !meta?.locked;

  // Optimistic UI update + close both modals
  setMeta((prev) => ({ ...prev, locked: newLocked }));
  setOpenLockModal(false);
  setOpenUnlockModal(false);

  try {
    // include the acting user like other updates do
    const session = await getUserSession();
    const payload = { id: meta.id, locked: newLocked };
    if (session?.username) payload.userID = session.username;

      const result = await API.projects.update(payload);
    // success: you can inspect result.data.updateProject if needed
    return result;
  } catch (err) {
    // revert optimistic update
    console.error("Failed to update lock state:", err);
    setMeta((prev) => ({ ...prev, locked: !newLocked }));

    // show more descriptive error for debugging
    const status = err?.response?.status;
    const data = err?.response?.data;
    console.error("Lock API error status:", status, "data:", data);

    alert(
      "Could not update project lock. " +
      (data?.detail || data?.message || (status ? `Server returned ${status}` : "Check console for details."))
    );
  }
};

  /**
   * Restores a deleted project so it is publicly accessible again.
   */
  const handleRestore = async () => {
    const user = await getUserSession();

    await API.projects.update({
      id: meta.id,
      userID: user.username,
      publishStatus: "published"
    });

    setTimeout(() => history.push("/admin/project-logs"), 1500);
  };

  /**
   * Creates an "Edit Project" button for the project publisher or staff.
   * @returns Edit Project Button
   */
  function getEditButton() {
    if (!user || !user.success) return null;
    const isPublisher = isProjectOwnedByUser(meta, user);
    const isStaff = user.is_staff === true || user.is_staff === "true";

    // Normalize locked flag (boolean, string, or numeric)
    const locked = !!(
      meta && (
        meta.locked === true ||
        meta.locked === "true" ||
        meta.locked === 1 ||
        meta.locked === "1"
      )
    );

    // Normalize possible user override flag
    const userEditLocked = user.edit_locked === true || user.edit_locked === "true";

    // Owner can edit only when not locked. Nobody can edit when locked.
    const canEdit = (isPublisher || isStaff) && !locked && !userEditLocked;

    if (!canEdit) return null;

    return (
      <button
        type="button"
        onClick={() => history.push("/EditProject?projectID=" + meta.id)}
        className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600"
        data-cy="edit-project"
      >
        Edit Project
      </button>
    );
  }

  /**
   * Creates a "Unlock/Lock Project" button depending on whether the project is locked down for editing or not.
   * @returns Unlock/Lock Project Button
   */
  function getLockButton() {
    if (meta.locked) {
      return (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-md shadow-sm bg-red-100 px-2 py-1 text-sm font-medium text-red-700 dark:bg-red-300 dark:text-red-900">
            <HiLockClosed className="relative mr-1.5 h-3.5 w-3.5 stroke-red-700/50" />
            Locked
          </span>
          <button
            type="button"
            onClick={() => setOpenUnlockModal(true)}
            className="rounded bg-white border border-gray-300 px-2 py-1 text-sm font-medium text-gray-900 shadow-sm hover:bg-gray-50"
            data-cy="unlock-edit"
          >
            Unlock Project
          </button>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-md shadow-sm bg-green-100 px-2 py-1 text-sm font-medium text-green-700 dark:bg-green-300 dark:text-green-900">
            <HiLockOpen className="relative mr-1.5 h-3.5 w-3.5 stroke-green-700/50" />
            Unlocked
          </span>
          <button
            type="button"
            className="rounded bg-white border border-gray-300 px-2 py-1 text-sm font-medium text-gray-900 shadow-sm hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-600"
            onClick={() => setOpenLockModal(true)}
            data-cy="lock-edit"
          >
            Lock Project
          </button>
        </div>
      );
    }
  }

  /**
   * Creates a section for downloadable files with each file showing up on a separate card that can contain file name, size, description, and download button.
   * @returns Downloadable files cards
   */
  function getDownloadableFiles() {
    return (
      <>
        <div className="col-span-2">
          <dt className="text-base font-medium text-gray-600 dark:text-gray-300">Attachments</dt>
          <dd className="mt-1 text-sm text-gray-900 grid md:grid-cols-2 gap-2">
            {files !== undefined && files.length > 0 && files !== "" ? (
              files.map((file, index) => {
                return (
                  <div
                    key={file.name}
                    className="pl-3 pr-4 py-3 flex justify-between items-center text-sm border border-gray-200 rounded-md shadow-sm dark:border-gray-700"
                  >
                    <div className="flex-1 flex items-center">
                      <FiPaperclip
                        className="flex-shrink-0 h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                      <div className="ml-2 flex-1 w-0">
                        <p className="truncate font-medium dark:text-white">{file.name}</p>
                        {/* File Description goes here. Waiting for feature to be added on submit page. VVVV */}
                        <div className="w-full overflow-hidden group-hover:overflow-visible group-hover:w-auto">
                          <p className="text-xs dark:text-gray-400">{file.description}</p>
                        </div>
                      </div>
                    </div>
                    <div className="ml-4 flex-shrink-0 inline-flex items-center">
                      <span className="mr-1.5 text-gray-600 text-xs text-right dark:text-gray-400">
                        {file.size}
                      </span>
                      <a
                        // onClick={() => {window.location.href = file.downloadLink}} // Can also use href attribute on the <a> tag. Hides download source when you inspect
                        role="button"
                        href={file.downloadLink}
                        data-cy="download"
                        className="font-medium text-blue-600 hover:text-blue-500 p-0.5 hover:bg-gray-100 rounded-md mx-auto dark:hover:bg-gray-700"
                      >
                        <span className="sr-only">Download file</span>
                        <HiOutlineDownload
                          className="h-6 w-6"
                          aria-hidden="true"
                        />
                      </a>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 dark:text-gray-400">No files available for download.</p>
            )}
          </dd>
        </div>
      </>
    );
  }

  /**
   * Creates a section for project contributors with each contributor showing up on a separate card that can contain the contributor name, affiliation, email, and role.
   * @returns Contributors cards
   */
  function getContributors() {
    if (meta.stakeholders === undefined) {
      return;
    }

    let stakeholderList = JSON.parse(meta.stakeholders);
    return stakeholderList.map((stakeholder, index) => {
      return (
        <div
          key={index}
          className="border border-gray-200 rounded-md shadow-sm p-4 text-sm lg:col-span-1 col-span-2 dark:border-gray-700"
        >
          <dl className="text-gray-500">
            <dd className="truncate text-gray-900 font-medium dark:text-white">
              {stakeholder.name}
            </dd>
            <dd className="mt-1 truncate text-blue-600 dark:text-gray-300">
              {stakeholder.affiliation}
            </dd>
            <dd className="mt-1 truncate dark:text-gray-400">{stakeholder.email}</dd>
            {stakeholder.role === "Other"
              ? stakeholder.inputRole
              : stakeholder.roles
                ? stakeholder.roles.map((role) => (
                  <dd className="mt-2 text-xs dark:text-gray-400">
                    • {role}

                  </dd>
                ))
                : <dd className="mt-2 text-xs dark:text-gray-400">• {stakeholder.role}</dd>}

          </dl>
        </div>
      );
    });
  }

  /**
   * Creates a section for grant information with each grant/award showing up on a separate card that contains award title, funder/sponsor name, and grant number.
   * @returns Grant Information cards
   */
  function getAwardInfo() {
    if (
      meta.sponsors === undefined ||
      meta.sponsors === "" ||
      meta.sponsors === null
    ) {
      return;
    }

    let sponsorList = JSON.parse(meta.sponsors);
    return sponsorList.map((sponsor, index) => {
      return (
        <div
          key={index}
          className="border border-gray-200 rounded-md shadow-sm p-4 text-sm lg:col-span-1 col-span-2 dark:border-gray-700"
        >
          <dl className="text-gray-600">
            <dd className="truncate sm:flex gap-x-2">
              <p className="text-gray-900 font-medium dark:text-white">Award Title:</p>{" "}
              <p className="text-gray-900 font-medium dark:text-gray-400">{sponsor.awardTitle}</p>
            </dd>
            <dd className="mt-2 truncate sm:flex gap-x-2">
              <p className="text-gray-900 font-medium dark:text-white">Funder Name:</p>{" "}
              <p className="text-gray-900 font-medium dark:text-gray-400">{sponsor.sponsor}</p>
            </dd>
            <dd className="mt-2 truncate sm:flex gap-x-2">
              <p className="text-gray-900 font-medium dark:text-white">Grant Number:</p>{" "}
              <p className="text-gray-900 font-medium dark:text-gray-400">{sponsor.grantNo}</p>
            </dd>
          </dl>
        </div>
      );
    });
  }

  /**
   * Creates a section for "Additional Information" which contains project coordinates in a table, research program name, status, and website.
   * @returns Additional Information section
   */
  function getAdditionalInfo() {
    return (
      <div className="col-span-2 grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-2 pt-6 border-t border-gray-200 dark:border-gray-700">
        {/* Data Extent */}
        <div className="col-span-2">
          <dt className="text-base font-medium text-gray-600 dark:text-gray-300">Data Extent</dt>
          <table className="mt-1 w-full divide-y divide-gray-300 shadow-sm">
            <tbody className="bg-gray-50 border border-gray-200 shadow-sm dark:border-gray-700">
              <tr className="bg-white dark:bg-gray-800">
                <td className="p-3 text-sm font-medium text-gray-900 dark:text-white">
                  North Bound Latitude
                </td>
                <td className="p-3 text-sm text-gray-500 dark:text-gray-400">
                  {meta.northLatitude}
                </td>
              </tr>
              <tr className="bg-gray-50 dark:bg-gray-900">
                <td className="p-3 text-sm font-medium text-gray-900 dark:text-white">
                  East Bound Longitude
                </td>
                <td className="p-3 text-sm text-gray-500 dark:text-gray-400">
                  {meta.eastLongitude}
                </td>
              </tr>
              <tr className="bg-white dark:bg-gray-800">
                <td className="p-3 text-sm font-medium text-gray-900 dark:text-white">
                  South Bound Latitude
                </td>
                <td className="p-3 text-sm text-gray-500 dark:text-gray-400">
                  {meta.southLatitude}
                </td>
              </tr>
              <tr className="bg-gray-50 dark:bg-gray-900">
                <td className="p-3 text-sm font-medium text-gray-900 dark:text-white">
                  West Bound Longitude
                </td>
                <td className="p-3 text-sm text-gray-500 dark:text-gray-400">
                  {meta.westLongitude}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Program Name, Status, Website */}
        <div className="grid grid-cols-2 gap-y-8 col-span-2">
          <div className="col-span-2 sm:col-span-1">
            <div className="sm:col-span-1">
              <dt className="text-base font-medium text-gray-600 dark:text-gray-300">
                Research Program Name
              </dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-400">
                {meta.researchProgramName !== null &&
                  meta.researchProgramName !== ""
                  ? meta.researchProgramName
                  : "N/A"}
              </dd>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-y-8 col-span-2 sm:col-span-1">
            <div className="col-span-1">
              <dt className="text-base font-medium text-gray-600 dark:text-gray-300">Status</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-400">
                {/* Why is the status stored as a number ಠ_ಠ */}
                {meta.status === 0 ? "Planned" : ""}
                {meta.status === 1 ? "In Progress" : ""}
                {meta.status === 2 ? "Completed" : ""}
              </dd>
            </div>
            <div className="col-span-1">
              <dt className="text-base font-medium text-gray-600 dark:text-gray-300">Website</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-400">
                {meta.website !== null && meta.website !== ""
                  ? meta.website
                  : "N/A"}
              </dd>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div ref={printRef} className="max-w-5xl mx-auto">
        <div className="relative bg-white border shadow-sm border-gray-200 overflow-hidden rounded-lg pt-0 mt-0 dark:bg-gray-800 dark:border-gray-700">
          <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-blue-500 to-blue-600"></span>{" "}
          {/* The blue strip on the top */}
          <div className="px-4 py-5 sm:px-6">
            {/* Title */}
            <h1 className="mt-2 text-xl leading-6 font-medium text-gray-900 dark:text-white">
              {meta.title}
            </h1>

            <div className="items-center flex flex-wrap gap-y-3 justify-between mt-1">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                {/* Publish Date */}
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Published on:{" "}
                  {meta.publishedAt ? new Date(meta.publishedAt).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  }) : 'N/A'}
                </p>
                {/* Status */}
                <p className="text-sm text-gray-500 inline-flex items-center dark:text-gray-400">
                  <HiOutlineCalendar
                    className="flex-shrink-0 h-5 w-5 text-gray-400 mr-1.5  dark:text-gray-300"
                    aria-hidden="true"
                  />
                  {meta.status === 0 ? "Planned" : ""}
                  {meta.status === 1 ? "In Progress" : ""}
                  {meta.status === 2 ? "Completed" : ""}
                </p>
                {/* DOI */}
                {meta.DOI !== "" && meta.DOI !== "Requested" && (
                  <a
                    href={"https://doi.org/" + meta.DOI}
                    target="_blank"
                    className="text-sm font-medium hover:underline text-blue-600 inline-flex items-center dark:text-blue-500"
                  >
                    <img
                      className="h-5 w-5 mr-1.5"
                      src={DoiLogo}
                      alt="DOI logo"
                      draggable="false"
                    />
                    <p>{meta.DOI}</p>
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* Edit Project Button */}
                {getEditButton()}
                {/* Lock Project Edit Button */}
                {isModerator && getLockButton()}
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 px-4 pt-5 pb-12 sm:px-6 dark:border-gray-700">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
              {/* Abstract */}
              <div className="col-span-2">
                <dt className="text-base font-medium text-gray-600 dark:text-gray-300">
                  Abstract
                </dt>
                <dd className="mt-1 text-sm text-gray-900 whitespace-pre-line dark:text-gray-400">{meta.abstract}</dd>
              </div>
              <div className="grid grid-cols-2 gap-y-8 col-span-2">
                <div className="col-span-2 sm:col-span-1">
                  {/* License */}
                  <div className="sm:col-span-1">
                    <dt className="text-base font-medium text-gray-600 dark:text-gray-300">
                      License
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {meta && licenses[meta.license] ? (
                        <a
                          href={licenses[meta.license].url}
                          target="_blank"
                          rel={meta.license}
                          className="hover:text-blue-600 hover:underline flex text-gray-900 dark:text-gray-400 dark:hover:text-blue-600"
                        >
                          {meta.license}
                          <HiExternalLink className="w-4 h-4 text-gray-600 ml-1"/>
                        </a>
                      ) : (
                        // Checks if the license is undefined before trying to parse it
                        meta.license ? meta.license : meta.license
                      )}
                    </dd>

                  </div>
                </div>
                <div className="grid grid-cols-2 gap-y-8 col-span-2 sm:col-span-1">
                  {/* Start Date */}
                  <div className="col-span-1">
                    <dt className="text-base font-medium text-gray-600 dark:text-gray-300">
                      Start Date
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-400">
                      {new Date(meta.startDate).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </dd>
                  </div>
                  {/* End Date */}
                  <div className="col-span-1">
                    <dt className="text-base font-medium text-gray-600 dark:text-gray-300">
                      End Date
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-400">
                      {" "}
                      {meta.endDate
                        ? new Date(meta.endDate).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                        : "TBD(In Progress)"}
                    </dd>
                  </div>
                </div>
              </div>

              {/* Files */}
              {getDownloadableFiles()}

              {/* Map / Project Area */}
              <div className="col-span-2">
                <dt className="text-base font-medium text-gray-600 dark:text-gray-300">
                  Project Area
                </dt>
                <div className="rounded-lg overflow-hidden mt-1">
                  <MapContainer
                    center={[69.116864, -105.059681]}
                    zoom={15}
                    style={{ height: "300px", zIndex: 0 }}
                    worldCopyJump={true}
                    maxBoundsViscosity={1}
                    minZoom={2}
                  >
                    <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}" />
                    <ProjectArea meta={meta} />
                  </MapContainer>
                </div>
              </div>

              {/* Keywords / Research Platforms */}
              <div className="sm:col-span-1 col-span-2">
                <dt className="text-base font-medium text-gray-600 dark:text-gray-300">
                  Keywords
                </dt>
                <div className="mt-1 flex flex-wrap">
                  {typeof meta.keywords !== "undefined" ? (
                    JSON.parse(meta.keywords).map((keyword) => (
                      <Link
                        key={keyword}
                        to={"/data-catalogue?keywords=" + keyword}
                        className="mr-2 mb-2 px-3 py-1 rounded-full text-gray-600 bg-gray-200 hover:bg-gray-300 font-medium text-sm flex align-center w-max active:bg-gray-300 transition duration-300 ease dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white"
                      >
                        {keyword}
                      </Link>
                    ))
                  ) : (
                    <p></p>
                  )}
                </div>
              </div>
              <div className="sm:col-span-1 col-span-2">
                <dt className="text-base font-medium text-gray-600 dark:text-gray-300">
                  Research Platforms
                </dt>
                <div className="mt-1 flex flex-wrap">
                  {typeof meta.vessels !== "undefined" ? (
                    JSON.parse(meta.vessels).map((vessel) => (
                      <Link
                        key={vessel}
                        to={"/data-catalogue?researchPlatform=" + vessel}
                        className="mr-2 mb-2 px-3 py-1 rounded-full text-gray-600 bg-gray-200 hover:bg-gray-300 font-medium text-sm flex align-center w-max active:bg-gray-300 transition duration-300 ease dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white"
                      >
                        {vessel}
                      </Link>
                    ))
                  ) : (
                    <p></p>
                  )}
                </div>
              </div>

              {/* Citation */}
              <div className="col-span-2">
                <dt className="text-base font-medium text-gray-600 dark:text-gray-300">
                  Citation
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-400">
                  {meta.citation === "" || meta.citation === null
                    ? "N/A"
                    : meta.citation}
                </dd>
              </div>

              {/* Contributors */}
              <div className="col-span-2">
                <Disclosure defaultOpen as="div">
                  {({ open }) => (
                    <>
                      <dt>
                        <Disclosure.Button className="flex w-full items-center text-left text-gray-400">
                          <span className="text-base font-medium text-gray-600 dark:text-gray-300">
                            Contributors
                          </span>

                          {open ? (
                            <HiOutlineMinusCircle
                              className="h-5 w-5 ml-2"
                              aria-hidden="true"
                            />
                          ) : (
                            <HiOutlinePlusCircle
                              className="h-5 w-5 ml-2"
                              aria-hidden="true"
                            />
                          )}
                        </Disclosure.Button>
                      </dt>
                      <Disclosure.Panel
                        as="dd"
                        className="mt-1 grid grid-cols-2 gap-2"
                      >
                        {getContributors()}
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              </div>

              {/* Grant Information*/}
              {meta.sponsors && (
                <div className="col-span-2">
                  <Disclosure as="div">
                    {({ open }) => (
                      <>
                        <dt>
                          <Disclosure.Button className="flex w-full items-center text-left text-gray-400">
                            <span className="text-base font-medium text-gray-600 dark:text-gray-300">
                              Grant Information
                            </span>
                            {open ? (
                              <HiOutlineMinusCircle
                                className="h-5 w-5 ml-2"
                                aria-hidden="true"
                              />
                            ) : (
                              <HiOutlinePlusCircle
                                className="h-5 w-5 ml-2"
                                aria-hidden="true"
                              />
                            )}
                          </Disclosure.Button>
                        </dt>
                        <Disclosure.Panel
                          as="dd"
                          className="mt-1 grid grid-cols-2 gap-2"
                        >
                          {getAwardInfo()}
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                </div>
              )}

              {/* Additional Information */}
              <div className="col-span-2">
                <Disclosure as="div" className="">
                  {({ open }) => (
                    <>
                      <dt>
                        <Disclosure.Button className="flex w-full items-center text-left text-gray-400">
                          <span className="text-base font-medium text-gray-600 dark:text-gray-300">
                            Additional Information
                          </span>
                          {open ? (
                            <HiOutlineMinusCircle
                              className="h-5 w-5 ml-2"
                              aria-hidden="true"
                            />
                          ) : (
                            <HiOutlinePlusCircle
                              className="h-5 w-5 ml-2"
                              aria-hidden="true"
                            />
                          )}
                        </Disclosure.Button>
                      </dt>
                      <Disclosure.Panel
                        as="dd"
                        className="mt-1 grid grid-cols-2 gap-2"
                      >
                        {getAdditionalInfo()}
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              </div>
            </dl>
          </div>
        </div>
        {/* Restore Project Button */}
        {meta.publishStatus === "archived" && (
          <div className="col-span-2 mb-2 flex justify-center mt-4">
            <button
              type="button"
              className="relative rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              onClick={() => setOpenRestoreModal(true)}
              data-cy="restore"
            >
              Restore Deleted Project
            </button>
          </div>
        )}
        {/* ух ты */}
      </div>
      <LockProjectModal
        setOpenModal={setOpenLockModal}
        openModal={openLockModal}
        toggleLock={toggleLock}
      />
      <UnlockProjectModal
        setOpenModal={setOpenUnlockModal}
        openModal={openUnlockModal}
        toggleLock={toggleLock}
      />
      <RestoreProjectModal
        setOpenModal={setOpenRestoreModal}
        openModal={openRestoreModal}
        restoreProject={handleRestore}
      />
    </>
  );
}
/* ඞ */
